import { nodeConfig } from '@scripts/mixins';
import { getVideoAPIByType } from '@scripts/modules/api/video';
import { appendScriptToBody, getMotionPreference, getDataPreference } from '@scripts/helpers/utils';
import * as CONSTANTS from '@scripts/helpers/constants';

const NAMESPACE = 'video-background';
const DEFAULT_SELECTOR = '[data-js-video-background]';

/**
 * @typedef {Object} VideoBackgroundSettings
 * @property {string} id - Video ID.
 * @property {string} type - 'youtube' | 'vimeo'.
 * @property {string|null} [controlId=null] - ID for remote controls.
 * @property {boolean} [autoplay=false] - Autoplay on load.
 * @property {string} [rootMargin='200px'] - Observer margin.
 */
const DEFAULT_SETTINGS = {
	controlId: null,
	autoplay: false,
	rootMargin: '200px'
};

/**
 * Auto playing background video.
 * @fires video-background:play
 * @fires video-background:pause
 * @fires video-background:ready
 */
class VideoBackground {
	/**
	 * @param {HTMLElement} node
	 * @param {VideoBackgroundSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.elIframeTarget = this.elNode.querySelector(`[data-js-${NAMESPACE}-iframe-target]`);
		this.elToggle = this.elNode.querySelector(`[data-js-${NAMESPACE}-toggle]`);
		this.remoteToggles = [];

		if (this.config.controlId) {
			this.remoteToggles = Array.from(
				document.querySelectorAll(
					`[data-js-${NAMESPACE}-toggle="${this.config.controlId}"]`
				)
			);
		}

		this.api = null;
		this.observer = null;
		this.isPlaying = false;
		this.isReady = false;
		this.wasPlayingBeforeHidden = false;
	}

	init() {
		if (!this.api) throw Error(`[${NAMESPACE}] Video API must be provided via setApi()`);
		this._setup();
	}

	destroy() {
		this._unbindUI();

		if (this.observer) this.observer.disconnect();

		this.elNode.classList.remove(CONSTANTS.LOADED_CLASS);
		this.setVisualState(false);
	}

	/** @returns {HTMLIFrameElement|null} */
	get elIframe() {
		return this.elNode.querySelector('iframe');
	}

	setApi(api) {
		this.api = api;
	}

	/** @param {boolean} isPlaying */
	setVisualState(isPlaying) {
		if (this.elToggle) {
			this.elToggle.classList.toggle(CONSTANTS.ACTIVE_CLASS, isPlaying);
		}

		this.remoteToggles.forEach((btn) => {
			btn.classList.toggle(CONSTANTS.ACTIVE_CLASS, isPlaying);
		});
	}

	toggle() {
		if (this.isPlaying) {
			this.pause();
		} else {
			this.play();
		}
	}

	muteAndPlay() {
		this.api.setVolumeByPercentage(0);
		return this.play();
	}

	play() {
		const playPromise = Promise.resolve(this.api.play());

		this.isPlaying = true;
		this.setVisualState(true);
		this._dispatch('play');

		return playPromise;
	}

	pause() {
		this.api.pause();

		this.isPlaying = false;
		this.setVisualState(false);
		this._dispatch('pause');
	}

	reveal() {
		this.elNode.classList.add(CONSTANTS.LOADED_CLASS);
	}

	_setup() {
		this.setVisualState(false);

		this.observer = new window.IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					this._onVisible();
				} else {
					this._onHidden();
				}
			},
			{ rootMargin: this.config.rootMargin }
		);

		this.observer.observe(this.elNode);
	}

	_bindUI() {
		this.toggle = this.toggle.bind(this);

		if (this.elToggle) this.elToggle.addEventListener('click', this.toggle);
		this.remoteToggles.forEach((btn) => btn.addEventListener('click', this.toggle));
	}

	_unbindUI() {
		if (this.elToggle) this.elToggle.removeEventListener('click', this.toggle);
		this.remoteToggles.forEach((btn) => btn.removeEventListener('click', this.toggle));
	}

	/** @param {string} action */
	_dispatch(action) {
		this.elNode.dispatchEvent(
			new CustomEvent(`${NAMESPACE}:${action}`, {
				bubbles: true,
				detail: { instance: this }
			})
		);
	}

	_onPlayerReady() {
		if (this.elIframe) {
			this.elIframe.setAttribute('tabindex', '-1');
			this.elIframe.setAttribute('aria-hidden', 'true');
		}

		this.isReady = true;
		this._bindUI();
		this._dispatch('ready');
	}

	_onScriptLoaded() {
		const config = Object.assign({}, this.config, {
			iframe: this.elIframeTarget,
			display: 'background'
		});

		return this.api.loadPlayer(config).then(this._onPlayerReady.bind(this));
	}

	_onVisible() {
		if (this.isReady) {
			if (this.wasPlayingBeforeHidden) {
				this.wasPlayingBeforeHidden = false;
				this.play();
			}
			return;
		}

		const shouldAutoplay =
			this.config.autoplay && !getMotionPreference() && !getDataPreference();

		let pipeline = appendScriptToBody(this.api.scriptUrl)
			.then(this._onScriptLoaded.bind(this))
			.then(() => this.reveal());

		if (shouldAutoplay) {
			pipeline = pipeline.then(() => this.muteAndPlay());
		}

		pipeline.catch(() => {});
	}

	_onHidden() {
		if (!this.isReady || !this.isPlaying) return;

		this.wasPlayingBeforeHidden = true;
		this.pause();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return [];

	return Array.from(listNode)
		.map((node) => {
			const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);

			if (!config.id || !config.type) return;

			const apiCtr = getVideoAPIByType(config.type);

			if (!apiCtr) {
				node.style.display = 'none';
				return;
			}

			const module = new VideoBackground(node, config);

			module.setApi(new apiCtr());
			module.init();

			return module;
		})
		.filter(Boolean);
}
